import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import { CREATEROOMTYPE, ROOMTYPEDETAIL } from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import fileUploader from "../../../../utilities/fileUploader";
import Loading from "../../../../components/Common/Loading";

const getSchema = (t) =>
  z.object({
    imageUrl: z.string().nonempty({ message: t("roomType.form.validation.imageRequired") }),
    name: z.string().nonempty({ message: t("roomType.form.validation.nameRequired") }),
    status: z.boolean(),
    description: z.string().optional(),
  });

const AddRoomType = () => {
  const { t } = useTranslation();
  const [roomTypeDetails, setRoomTypeDetails] = useState(null);
  const { id } = useParams();
  const schema = useMemo(() => getSchema(t), [t]);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imgloader, setImgLoader] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      imageUrl: "",
      name: "",
      status: true,
      description: "",
    },
  });

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      ...(id && { _id: id }),
      status: data.status ? "Active" : "Inactive",
    };
    console.log(body, "body value");

    const res = await (id ? CREATEROOMTYPE(body) : CREATEROOMTYPE(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate("/roomtype");
    }
  });

  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);
    const file = e.target.files[0];
    const res = await fileUploader(file);

    if (res?.url) {
      setValue("imageUrl", res.url, { shouldValidate: true });
      setImagePreview(res.url);
    }

    setImgLoader(false);
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ROOMTYPEDETAIL({ id: id });

    const success = checkResponse({
      res,
      setData: setRoomTypeDetails,
      setLoader,
    });

    if (success && res?.data) {
      reset({
        imageUrl: res?.data?.data?.imageUrl || "",
        name: res?.data?.data?.name || "",
        status: res?.data?.data?.status === "Active",
        description: res?.data?.data?.description || "",
      });
      setImagePreview(res?.data?.data?.imageUrl || null);
    }
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => {
    navigate("/roomtype");
  };

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/roomtype"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      stroke-width="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("roomType.form.edit") : t("roomType.form.add")}
                </h4>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("roomType.form.imageLabel")}
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleImageChange}
                        />
                        {errors?.imageUrl && (
                          <p className="text-danger m-0">
                            {errors?.imageUrl?.message}
                          </p>
                        )}
                        {imgloader ? (
                          <div
                            style={{ height: 140, width: 140 }}
                            className="img-fluid rounded object-fit-cover"
                          >
                            <Loading fullSize={true} />
                          </div>
                        ) : (
                          imagePreview && (
                            <img
                              src={imagePreview}
                              alt={t("roomType.form.previewAlt")}
                              style={{ width: "100%", marginTop: "10px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>

                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          {t("roomType.form.nameLabel")}
                        </label>
                        <input
                          type="text"
                          placeholder={t("roomType.form.namePlaceholder")}
                          className="form-control"
                          {...register("name")}
                        />
                        {errors?.name && (
                          <p className="text-danger m-0">
                            {errors?.name?.message}
                          </p>
                        )}
                      </div>
                    </Col>

                    <Col lg="12" className="my-3">
                      <div className="d-flex align-items-center gap-3 px-4">
                        <Form.Check
                          type="switch"
                          id="status-switch"
                          label={t("roomType.form.status")}
                          {...register("status")}
                          style={{ transform: "scale(1.4)", cursor: "pointer" }}
                        />
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          type="submit"
                          className="d-flex align-items-center justify-content-center commonBtn"
                        >
                          {t("common.submit")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddRoomType;
